/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState, useEffect } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { adminApi } from '../../../api';
import { formatDateAndTime, isAdminView, truncateSilaId } from '../../../utils';
import { HANDLE } from '../../../constants';
import { useAlerts } from '../../context';

import DataList from '../DataList';
import HeaderSearchForm from '../HeaderSearchForm';
import {
  CheckboxColumnFilter,
  DateBetweenFilter,
  DateRangeColumnFilter,
  SelectColumnFilter,
} from '../table/TableFilters';
import TooltipButton from '../TooltipButton';

const TABLE_MAX_COUNT = 20;
const TABLE_SLUG = 'end_users';
const INDIVIDUAL = 'individual';
const BUSINESS = 'business';

const HIDE_COLUMN_FOR_CUSTOMER = new Set([
  'last_verification_time',
  'entity_type',
  'verification_statuses',
  'sila_id',
]);

const HIDE_COLUMN_FOR_NON_CUSTOMER = new Set(['phone']);

function StatusBadges({ values, statuses }) {
  const [showAllBadges, setShowAllBadges] = React.useState(false);
  const { t } = useTranslation();

  const formatSingleStatusBadge = (value, multiple) => {
    let label = t('common:status.loading');
    const applicableStatus = statuses.find(
      (status) => (value?.value && status.name === value.value) || (value && status.name === value)
    );

    if (applicableStatus && applicableStatus.label) label = applicableStatus.label;

    let variant = 'primary';
    if (typeof value === 'string' && value.includes('passed')) {
      variant = 'success';
    } else if (typeof value === 'string' && value.includes('failed')) {
      variant = 'warning';
    }
    if (label === t('common:status.loading')) variant = 'info';

    return (
      <Badge
        pill
        key={value}
        className={`badge-outline py-2 px-3 w-100 d-block mx-auto badge-${variant}${
          multiple ? ' m-1' : ''
        }`}
      >
        {label}
      </Badge>
    );
  };

  const renderBadges = () => {
    if (!Array.isArray(values) || !values.length) return formatSingleStatusBadge(values);
    if (values.length === 1) return formatSingleStatusBadge(values[0]);

    const badges = values.map((val, i) =>
      formatSingleStatusBadge(val, values.length >= 2 && i !== values.length - 1)
    );

    if (!showAllBadges) {
      return (
        <div className="d-flex align-items-center">
          {badges[0]}
          <Button
            variant="link"
            className="text-info text-decoration-none p-0 ms-1"
            onClick={() => setShowAllBadges(true)}
          >
            +
          </Button>
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center">
        <div>{badges}</div>
        <Button
          variant="link"
          className="text-info text-decoration-none p-0 ms-1"
          onClick={() => setShowAllBadges(false)}
        >
          -
        </Button>
      </div>
    );
  };
  return renderBadges();
}

const formatSortBy = (sortBy) => {
  let query = sortBy;
  const name = sortBy.find((sort) => sort.id === 'name');
  if (name) {
    query = [
      ...query.filter((sort) => sort.id !== 'name'),
      { id: 'full_name', desc: name.desc },
      { id: 'first_name', desc: name.desc },
    ];
  }
  return { url: sortBy, query };
};

const formatFilters = (filters) => {
  let query = filters;
  const verificationTimestamp = filters.find((filter) => filter.id === 'last_verification_time');
  if (verificationTimestamp) {
    if (verificationTimestamp.value) {
      const [start, end] = verificationTimestamp.value;
      const startUTC = new Date(start).toISOString();
      const endUTC = new Date(end).toISOString();

      query = [
        ...query.filter((filter) => filter.id !== 'begins_at'),
        { id: 'last_verification_time_start', value: startUTC },
        { id: 'last_verification_time_end', value: endUTC },
      ];
    }
  }
  return { url: filters, query };
};

function EndUsersList({
  customer,
  filterName,
  fetchData,
  fetchOptions,
  generateRowUrl,
  hasPermissions,
  usePrevNext = false,
}) {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [statuses, setStatuses] = useState([]);

  const getVerificationStatus = async () => {
    try {
      const response = await adminApi.getVerificationStatuses();
      if (response.success) {
        setStatuses(response.verification_statuses);
      }
    } catch (error) {
      setAlert({ message: error, variant: 'danger' });
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        id: 'last_verification_time',
        Header: t('admin.endUsers.labels.verificationTime'),
        accessor: 'last_verification_update',
        filter: DateBetweenFilter,
        Filter: DateRangeColumnFilter,
        Cell: ({ value }) =>
          value ? (
            formatDateAndTime(value)
          ) : (
            <span className="text-meta">{t('common:labels.na')}</span>
          ),
      },
      {
        id: 'name',
        Header: t('common:labels.name'),
        accessor: (data) => data.full_name || `${data.first_name} ${data.last_name}`,
        searchOptions: { allowSpace: true },
        Filter: CheckboxColumnFilter,
        disableSortBy: customer,
        Cell: ({ value }) => (
          <div
            className="text-break d-block position-relative"
            style={{ maxWidth: 160, minWidth: 160 }}
          >
            {value || <span className="text-meta">{t('common:labels.na')}</span>}
          </div>
        ),
      },
      {
        id: 'entity_type',
        Header: t('admin.endUsers.labels.entity_type'),
        accessor: 'entity_type',
        Filter: SelectColumnFilter,
        disableSortBy: true,
        Cell: ({ row }) => (
          <i className={`sila-icon m-2 text-primary text-lg ms-n4 ${row.original.entity_type}`} />
        ),
        filterOptions: () => [
          { label: t('common:labels.individual'), value: INDIVIDUAL },
          { label: t('common:labels.business'), value: BUSINESS },
        ],
      },
      {
        id: 'handle',
        Header: t('common:labels.handle'),
        accessor: 'user_handle',
        Filter: CheckboxColumnFilter,
        disableSortBy: customer,
        Cell: ({ value }) => (
          <span className="text-break d-block" style={{ maxWidth: 160, minWidth: 160 }}>
            {value}
          </span>
        ),
      },
      {
        id: 'app_handle',
        Header: t('common:labels.app'),
        accessor: 'apps',
        Cell: ({ value }) =>
          value.map((item) => (
            <span
              key={item}
              className="text-break d-block"
              style={{ maxWidth: 160, minWidth: 160 }}
            >
              {item}
            </span>
          )),
        Filter: CheckboxColumnFilter,
        disableSortBy: customer,
      },
      {
        id: 'email',
        Header: t('admin.labels.email'),
        accessor: 'emails',
        Cell: ({ value }) =>
          value.map((item) =>
            value ? (
              <span
                key={item}
                className="text-break d-block"
                style={{ maxWidth: 200, minWidth: 160 }}
              >
                {item}
              </span>
            ) : (
              <span className="text-meta">{t('common:labels.na')}</span>
            )
          ),
        Filter: CheckboxColumnFilter,
        disableSortBy: customer,
      },
      {
        id: 'phone',
        Header: t('common:labels.phoneNumber'),
        accessor: 'phones',
        searchOptions: { onChange: (value) => value.replace(/\D/g, '') },
        Filter: CheckboxColumnFilter,
        disableSortBy: customer,
        Cell: ({ value }) => (
          <span className="text-break d-block" style={{ maxWidth: 160, minWidth: 160 }}>
            {value}
          </span>
        ),
      },
      {
        id: 'verification_statuses',
        Header: (
          <>
            {t('admin.endUsers.labels.kycStatus')}
            <TooltipButton
              placement="top"
              variant="link"
              text={t('admin.endUsers.labels.kycStatusFilterDisclaimer')}
              className="ms-2 no-underline p-0"
            >
              <i className="sila-icon info text-lg" />
            </TooltipButton>
          </>
        ),
        accessor: 'verification_statuses',
        Filter: CheckboxColumnFilter,
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadges values={value} statuses={statuses} />,
        filterOptions: () =>
          statuses.map((status) => ({ value: status.name, label: status.label })),
      },
      {
        id: 'sila_id',
        Header: t('common:labels.silaID'),
        accessor: 'sila_id',
        Filter: CheckboxColumnFilter,
        Cell: ({ value }) => truncateSilaId(value),
        disableSortBy: true,
      },
    ];

    const hide = customer ? HIDE_COLUMN_FOR_CUSTOMER : HIDE_COLUMN_FOR_NON_CUSTOMER;
    return cols.filter((col) => !hide.has(col.id));
  }, [customer, statuses]);

  useEffect(() => {
    if (!customer) getVerificationStatus();
  }, [customer]);

  return (
    <DataList
      headerLeftComponent={() => <HeaderSearchForm generateUrl={generateRowUrl} type={HANDLE} />}
      slug={TABLE_SLUG}
      limit={TABLE_MAX_COUNT}
      filterName={filterName}
      fetchData={fetchData}
      fetchOptions={fetchOptions}
      columns={columns}
      generateRowUrl={generateRowUrl}
      onFilter={formatFilters}
      onSortBy={formatSortBy}
      hasPermissions={hasPermissions}
      delayStart={isAdminView()}
      loadingMessage={t('admin.endUsers.loading')}
      usePrevNext={usePrevNext}
    />
  );
}

EndUsersList.propTypes = {
  /**
   * The unique item slug for the data list
   */
  filterName: PropTypes.string,
  /**
   * The fetch data function
   */
  fetchData: PropTypes.func.isRequired,
  /**
   * The fetch options object
   */
  fetchOptions: PropTypes.object.isRequired,
  /**
   * Generates a url as a destination for the row click
   */
  generateRowUrl: PropTypes.func.isRequired,
  /**
   * The function to validate user permissions
   */
  hasPermissions: PropTypes.func.isRequired,
};

export default EndUsersList;
